import React from "react";
import { Box, Grid, InputLabel } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { useTypedSelector } from "../../hooks.tsx";

interface TablePromptProps {
  children?: React.ReactNode;
  content: {
    rows: Array<{ [key: string]: any }>;
    columns: GridColDef[];
    clarification?: string;
  };
  feedback?: string | null;
  exchange_id?: string;
}

// Mini-example: DataGrid expects data in a specific format:
{
  /* <DataGrid
  columns={[{ field: 'username' }, { field: 'age' }]}
  rows={[
    {
      id: 1,
      username: '@MUI',
      age: 20,
    },
  ]}
/> */
}

export const validateTable = (rows: any[], columns: any[]): boolean => {
  // Check if we have data
  if (!rows || !columns || rows.length === 0 || columns.length === 0) {
    return false;
  }

  // Validate column structure
  if (
    !columns.every(
      (col) => typeof col === "object" && col !== null && "field" in col,
    )
  ) {
    return false;
  }

  // Get all column field names
  const columnFields = columns.map((col) => col.field);

  // Check each row has all required columns
  return rows.every((row) => {
    // Check if row is an object
    if (typeof row !== "object" || row === null) {
      return false;
    }

    // Check if row has all required columns
    return columnFields.every((field) => field in row);
  });
};

const getColumnType = (
  rows: any[],
  field: string,
): "string" | "number" | "boolean" | "date" => {
  if (rows.length === 0) return "string";

  const firstValue = rows[0][field];
  if (typeof firstValue === "number") return "number";
  if (typeof firstValue === "boolean") return "boolean";
  if (firstValue instanceof Date) return "date";
  return "string";
};

const TablePrompt: React.FC<TablePromptProps> = ({
  feedback,
  exchange_id,
  content: { rows, columns, clarification },
}) => {
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);

  // Transform data to include unique IDs for DataGrid
  const rowsWithIds = rows.map((row, index) => ({
    id: index,
    ...row,
  }));

  const styledColumns = columns.map((col) => {
    const columnType = getColumnType(rowsWithIds, col.field);
    return {
      ...col,
      headerClassName: "custom-column-header",
      flex: 1,
      type: columnType,
    };
  });

  return (
    <Box marginBottom={"4%"} marginLeft={"5%"}>
      <Grid
        container
        direction="row"
        spacing={2}
        alignContent="flex-start"
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 1, lg: 1 }}
          sx={{
            marginTop: "1%",
          }}
        >
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 11, lg: 11 }}>
          <InputLabel
            sx={{
              color: "black",
              whiteSpace: "normal",
            }}
          >
            <div>
              {validateTable(rowsWithIds, styledColumns) ? (
                <DataGrid
                  hideFooterSelectedRowCount
                  rows={rowsWithIds}
                  columns={styledColumns}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10, page: 0 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  showToolbar
                />
              ) : (
                <Box sx={{ p: 2, color: "error.main" }}>
                  Invalid table data. Please check that all required columns are
                  present.
                </Box>
              )}
            </div>
            {clarification && (
              <CustomReactMarkDown
                content={clarification}
              ></CustomReactMarkDown>
            )}
          </InputLabel>
        </Grid>
      </Grid>
      <Grid position={"relative"} sx={{ marginLeft: "8%", marginBottom: "5%" }}>
        <Grid container direction="row" position={"absolute"}>
          {feedback !== "bad" && (
            <Grid>
              <ThumbUpPrompt
                exchange_id={exchange_id}
                clicked={feedback === "good"}
              />
            </Grid>
          )}
          {feedback !== "good" && (
            <Grid>
              <ThumbDownPrompt
                exchange_id={exchange_id}
                clicked={feedback === "bad"}
              />
            </Grid>
          )}
          {isLoggedIn && (
            <Grid>
              <ExplainPrompt exchange_id={exchange_id} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TablePrompt;
